/**
 * Finding a product within the family on show, and knowing how many there are.
 *
 * Both controls submit to the backend rather than filtering the page in the
 * browser: the list is paginated server-side, so a local filter would search
 * only the page in front of the administrator and quietly report that a product
 * on page two does not exist.
 *
 * Neither control selects a family. This toolbar used to carry an "All
 * families" dropdown over the stored categories, and that became a second owner
 * of the same state the moment product management gained permanent family tabs
 * — two controls writing one selection, each able to contradict the other. The
 * tabs own it; what is left here narrows whatever they have selected.
 */

/** Lifecycle values the backend's status filter accepts.
 *
 * "archived" is offered because ProductService.list_products excludes archived
 * products unless it is asked for them by name — there is no separate archived
 * flag to invent, and no other way to reach one from this screen.
 */
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All active" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "unpublished", label: "Unpublished" },
  { value: "archived", label: "Archived" },
];

export function ProductListToolbar({
  search,
  status,
  total,
  onSearch,
  onStatus,
}: {
  search: string;
  status: string;
  /** null until the backend has answered, so no count is shown while loading. */
  total: number | null;
  onSearch: (next: string) => void;
  onStatus: (next: string) => void;
}) {
  return (
    <div className="productList__toolbar">
      <div className="productList__search">
        <label className="sr-only" htmlFor="product-search">
          Search products
        </label>
        {/* One box, because the backend matches the name, the merchant code and
            the slug in a single pass. Splitting it into two fields would ask the
            administrator to know which one the product was filed under. */}
        <input
          id="product-search"
          type="search"
          placeholder="Search by product name or product code"
          defaultValue={search}
          onKeyDown={(event) => {
            if (event.key === "Enter") onSearch(event.currentTarget.value);
          }}
          onBlur={(event) => {
            if (event.currentTarget.value !== search) {
              onSearch(event.currentTarget.value);
            }
          }}
        />
      </div>

      <div className="productList__filter">
        <label className="sr-only" htmlFor="status-filter">
          Lifecycle status
        </label>
        <select
          id="status-filter"
          value={status}
          onChange={(event) => onStatus(event.target.value)}
        >
          {STATUS_FILTERS.map((option) => (
            <option key={option.value || "all"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="spacer" />

      {total !== null ? (
        <span className="muted num">
          {total} product{total === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}
